/**
 * Sybotanica Kaching Cart — gift engine.
 * --------------------------------------
 * Replaces Kaching's market-bound progress bar with one driven by
 * the visitor's _geo_country cart attribute. The threshold map is
 * server-rendered into window.SybaGiftConfig by
 * snippets/nf-market-tiers.liquid.
 *
 * Phase 1 (THIS FILE):
 *   - Hide Kaching's existing progress bar (CSS + JS fallback)
 *   - Render our own bar with the correct per-country threshold
 *   - Update on every cart change (cart/add, cart/change, cart/clear)
 *
 * Phase 2 (NEXT — gated behind cfg.auto_manage_gift === true):
 *   - Auto-add the Mystery Gift line when subtotal crosses the gift
 *     threshold; auto-remove when it drops below.
 *   - Force qty=1 on the gift line if anyone bypasses the UI guards.
 *
 * HARD RULES:
 *   - Skip entirely in B2B contexts (no gift logic on wholesale).
 *   - Don't touch the cart in phase 1 — Kaching still owns gift add/remove.
 *   - Never throw; cart-drawer breakage is bad. Wrap every entry point.
 */
(function () {
  'use strict';

  /* ---- B2B exclusion. Mirrors assets/syba-geo-country-bridge.js. ---- */
  try {
    if (window.SybotanicaB2B && (window.SybotanicaB2B.isB2B || window.SybotanicaB2B.isB2BPage)) {
      return;
    }
  } catch (e) { /* fall through */ }

  var cfg = window.SybaGiftConfig;
  if (!cfg || typeof cfg.shipping_threshold_cents !== 'number' ||
      typeof cfg.gift_threshold_cents !== 'number') {
    return;
  }

  /* ---- Client-side per-country resolution -------------------------------
     The server (snippets/nf-market-tiers.liquid) bakes cfg for the DEFAULT
     (Ireland) market on a cold load of the English root, because the
     visitor's _geo_country isn't known at SSR time. window.SybaGiftConfigTable
     carries every market's values; here we re-point cfg at the visitor's
     detected _geo_country (written by assets/syba-geo-country-bridge.js) so
     the bar / shipping line / gift threshold are correct on first drawer
     paint WITHOUT requiring a click on the country picker.

     In practice this only adjusts EUROZONE markets (same EUR currency as the
     root market — only the threshold NUMBERS differ). SE/DK visitors are
     redirected by the geo bridge to /en-se//en-dk/, where the server already
     bakes the correct SEK/DKK market, so applyCountry is a no-op for them
     (cfg.iso already equals the detected ISO). */
  var TABLE = window.SybaGiftConfigTable || {};
  var appliedIso = (cfg.iso || '').toUpperCase();

  // Only re-resolve from _geo_country on the English ROOT (default market).
  // On a real localized market subfolder (/en-se/, /de-de/, …) the server
  // already baked the authoritative market into cfg, and a stale
  // _geo_country attribute must NOT override it (e.g. an old NL attribute on
  // the SE market would wrongly switch the SEK bar to EUR).
  var ON_DEFAULT_ROOT = (function () {
    try {
      return !(window.Shopify && window.Shopify.routes && window.Shopify.routes.root &&
               window.Shopify.routes.root !== '/');
    } catch (e) { return true; }
  })();

  function applyCountry(iso) {
    if (!ON_DEFAULT_ROOT) return false;
    if (!iso) return false;
    iso = String(iso).toUpperCase();
    if (iso === appliedIso) return false;
    var row = TABLE[iso];
    if (!row) return false;
    cfg.currency = row.currency;
    cfg.currency_symbol = row.currency_symbol;
    cfg.currency_is_suffix = row.currency_is_suffix;
    cfg.shipping_threshold_cents = row.shipping_threshold_cents;
    cfg.gift_threshold_cents = row.gift_threshold_cents;
    cfg.ship_cost_cents = row.ship_cost_cents;
    appliedIso = iso;
    lastRenderedTotal = -1; // force the bar to redraw with the new thresholds
    lastGiftTotal = -1;     // re-evaluate the gift threshold
    lastManageTotal = -1;   // re-open the manageGift fetch-gate (thresholds moved)
    return true;
  }

  // Read _geo_country from the cart and re-resolve. cb(countryChanged) fires
  // after — the flag MUST stay country-only: init's one-shot 800ms retry
  // re-runs this when the country did NOT change yet, and folding the
  // requires-shipping/grant flags into it would kill that retry on effectively
  // every first visit (empty cart flips requires_shipping). Flag flips redraw
  // inline instead. Also the guaranteed first-paint source for both flags
  // (this /cart.js fetch always runs on init).
  function resolveCountry(cb) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var countryChanged = applyCountry(c && c.attributes && c.attributes._geo_country);
        if (noteRequiresShipping(c)) queueRender();
        if (noteGrantedBundle(c)) queueRender();
        if (cb) cb(countryChanged);
      })
      .catch(function () { if (cb) cb(false); });
  }

  // FAST country signal published by the geo bridge (assets/syba-geo-country-
  // bridge.js): a synchronously-readable cache so the FIRST render already uses
  // the visitor's country instead of the SSR-baked Ireland default. This is the
  // fix for "shipping shows €9.95 (IE) instead of €5.95 (NL) on the root":
  // resolveCountry() above only learns the country AFTER the bridge's
  // _geo_country cart-attribute round-trip, so the drawer could paint the IE
  // ship_cost_cents first. window.SybaDetectedCountry is set when the bridge
  // resolves on THIS page; localStorage carries it across visits.
  function cachedIso() {
    try { if (window.SybaDetectedCountry) return window.SybaDetectedCountry; } catch (e) {}
    try { return localStorage.getItem('syba_geo_iso') || ''; } catch (e) { return ''; }
  }

  /* ---- Effective free-shipping threshold ----------------------------
     cfg.shipping_threshold_cents is the NATIVE per-country value (table row
     from snippets/nf-market-tiers.liquid). What the bar and the footer
     shipping line must use is min(native, the visitor's Intelligems
     free-shipping group threshold), resolved by assets/syba-ship-threshold.js
     (window.SybaShipThreshold, loaded in <head>). Without the resolver, or
     on a non-store-currency page, this is just the native value. Read
     live on every render, never cached into cfg, so applyCountry can keep
     overwriting cfg with the native row. */
  function shipThresholdCents() {
    var n = cfg.shipping_threshold_cents;
    try {
      var R = window.SybaShipThreshold;
      if (R && typeof R.effectiveCents === 'function') {
        var c = R.effectiveCents(n, cfg.currency);
        if (typeof c === 'number' && c > 0) return c;
      }
    } catch (e) { /* fall back to native */ }
    return n;
  }

  /* ---- Effective Mystery Gift threshold -----------------------------
     cfg.gift_threshold_cents is the per-country TABLE value. When the
     free-shipping threshold moves up (launch switch, e.g. NL 50 -> 70) the
     gift must stay a real step above it, so:
       gift = max(table gift, effective free-shipping threshold + gap)
     gap = cfg.gift_min_gap_cents (theme setting gift_min_gap_cents, EUR
     cents, default 2000; 0 or missing = rule off).
     EUR markets only: the gap is in store-currency cents, and the
     Intelligems half of the ship threshold is only applied when the page
     currency is the store currency too (assets/syba-ship-threshold.js).
     SEK/DKK keep their table gift value.
     Every gift-threshold reader (bar milestone + text, manageGift add/remove)
     goes through here, live, never cached into cfg: applyCountry overwrites
     cfg with the native row and the resolver can change after first paint.
     With today's table and no override / experiment the result equals the
     table everywhere (tests/gift-engine-gift-gap.mjs pins that per row). */
  function giftThresholdCents() {
    var g = cfg.gift_threshold_cents;
    try {
      var gap = cfg.gift_min_gap_cents;
      if (typeof gap !== 'number' || !isFinite(gap) || gap <= 0) return g;
      var shop = String(cfg.shop_currency || 'EUR').toUpperCase();
      if (String(cfg.currency || '').toUpperCase() !== shop) return g;
      var floor = shipThresholdCents() + gap;
      return (typeof floor === 'number' && floor > g) ? floor : g;
    } catch (e) { return g; }
  }

  // Console/QA hook: SybaGiftEngine.thresholds() on any storefront page.
  try {
    window.SybaGiftEngine = {
      thresholds: function () {
        return {
          iso: appliedIso,
          currency: cfg.currency,
          nativeShipCents: cfg.shipping_threshold_cents,
          shipCents: shipThresholdCents(),
          tableGiftCents: cfg.gift_threshold_cents,
          giftMinGapCents: cfg.gift_min_gap_cents,
          giftCents: giftThresholdCents()
        };
      }
    };
  } catch (e) {}

  var INJECTED_CLASS = 'syba-gift-pb';

  /* ---- Localized copy.
     Source of truth is window.SybaGiftCopy, emitted by
     snippets/nf-market-tiers.liquid via Shopify's | t filter (resolves
     to the visitor's URL locale). Strings mirror Kaching's own per-locale
     translations. The English fallback object here only kicks in if the
     snippet failed to load — it should never be the rendered copy in
     normal operation.

     bar_text uses [amount] + [reward] tokens substituted below — same
     single-template pattern Kaching uses ("Add {{progress_needed}} to
     unlock {{next_promo_title}}!"), so the bar reads identically whether
     the next reward is free shipping or the gift. */
  var COPY = window.SybaGiftCopy || {
    bar_text: 'Add [amount] to unlock [reward]!',
    bar_text_gift: 'Add [amount] to unlock your [r]Autumn Mystery Gift[/r]!',
    bar_text_shipping: 'Add [amount] to unlock [r]free shipping[/r]!',
    all_unlocked: 'All rewards unlocked! 🎉',
    label_shipping: 'Free shipping',
    label_gift: 'Autumn Mystery Gift',
    badge_new: 'NEW'
  };

  /* Per-reward full sentences (bar_text_gift / bar_text_shipping) exist
     because splicing a bare label into one shared template can't be
     grammatical everywhere: German needs an article that depends on the
     noun ("bis zu deiner Herbstüberraschung" / "bis zum kostenlosen
     Versand"), Finnish needs a case ending ("syksyn yllätyslahjan").
     [r]...[/r] marks the reward words to bold. A sentence that is not a
     string, lacks [amount] or reads "translation missing: …" falls back to
     the generic bar_text + [reward]. Note: a locale file that simply LACKS
     the key gets Shopify's en.default sentence (English), not "translation
     missing" — what guarantees every served locale has its own sentences is
     tests/gift-engine-bar-sentences.mjs (locale discovery + snippet check). */
  function usableSentence(s) {
    return typeof s === 'string' && s.indexOf('[amount]') !== -1 &&
      s.indexOf('translation missing') === -1;
  }

  function barText(remainingCents, reward, sentence) {
    var amount = '<strong>' + formatMoney(remainingCents) + '</strong>';
    if (usableSentence(sentence)) {
      return sentence
        .replace('[amount]', amount)
        .replace('[r]', '<strong>')
        .replace('[/r]', '</strong>');
    }
    return COPY.bar_text
      .replace('[amount]', amount)
      .replace('[reward]', '<strong>' + reward + '</strong>');
  }

  function formatMoney(cents) {
    var n = (cents / 100);
    // EUR + SEK/DKK conventionally use comma decimal in our markets.
    var s = n.toFixed(2).replace('.', ',');
    if (cfg.currency_is_suffix) return s + ' ' + cfg.currency_symbol;
    return cfg.currency_symbol + s;
  }

  /* ---- Cart total resolution.
     Try Kaching's documented API first (synchronous, no network).
     Fall back to /cart.js fetch if Kaching's API isn't ready (initial
     page load before drawer has been opened, hot-reload, etc.). */
  function getCartTotalSync() {
    try {
      if (window.kachingCartApi && typeof window.kachingCartApi.getCartData === 'function') {
        var d = window.kachingCartApi.getCartData();
        if (d) { noteRequiresShipping(d); noteGrantedBundle(d); if (typeof d.total_price === 'number') return d.total_price; }
      }
    } catch (e) { /* fall through */ }
    return null;
  }

  function getCartTotalAsync() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        noteRequiresShipping(c);
        noteGrantedBundle(c);
        return (c && typeof c.total_price === 'number') ? c.total_price : 0;
      })
      .catch(function () { return 0; });
  }

  /* ---- Bar markup.
     Two EVENLY-SPACED milestones (truck at 50%, gift at 100%) — matches
     Kaching's segmented bar. We deliberately do NOT position them
     proportionally to the threshold values, because when the two
     thresholds are numerically close (e.g. €70 / €95) the icons + labels
     bunch up and overlap. Instead the fill maps cart progress onto the
     segmented scale:
        total < ship          → fill 0 .. 50%   (progress to free shipping)
        ship <= total < gift  → fill 50 .. 100% (progress to mystery gift)
        total >= gift         → fill 100%
     Labels sit below the track; the last (gift) label is right-anchored
     so it never clips past the track's right edge. */
  function buildBarHTML(totalCents) {
    var shipCents = shipThresholdCents();
    var giftCents = giftThresholdCents();
    var SEG = 50; // 100% / 2 tiers

    // An M/L Welcome Bundle unlocks the shipping milestone regardless of
    // total (checkout-enforced grant) — the bar then only tracks the gift.
    // Below the ship threshold the gift segment shows no progress yet
    // (fill parks at 50%): honest, since the gift still keys off the total.
    var shipUnlocked = cartHasGrantedBundle || totalCents >= shipCents;

    var fillPct;
    if (totalCents >= giftCents) {
      fillPct = 100;
    } else if (shipUnlocked) {
      var giftProgress = totalCents - shipCents;
      if (giftProgress < 0) giftProgress = 0;
      // Denominator guard: gift <= ship thresholds would make this 0/0 = NaN
      // (invalid CSS width). Reachable below the ship threshold now that the
      // grant unlocks this branch at any total. Park at the ship milestone.
      var giftSpan = giftCents - shipCents;
      fillPct = giftSpan > 0 ? SEG + (giftProgress / giftSpan) * SEG : SEG;
    } else {
      fillPct = (totalCents / shipCents) * SEG;
    }
    fillPct = Math.min(100, Math.max(0, fillPct));

    var text;
    var textClass = 'syba-gift-pb__text';
    if (totalCents >= giftCents) {
      text = COPY.all_unlocked;
      textClass += ' syba-gift-pb__text--success';
    } else if (shipUnlocked) {
      text = barText(giftCents - totalCents, COPY.label_gift, COPY.bar_text_gift);
    } else {
      text = barText(shipCents - totalCents, COPY.label_shipping, COPY.bar_text_shipping);
    }

    var shipReached = shipUnlocked ? ' is-reached' : '';
    var giftReached = totalCents >= giftCents ? ' is-reached' : '';

    // Truck icon (Material-style, simplified)
    var truckSvg = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
      '<path d="M3 4h12v9H3V4zm12 3h3l3 4v4h-2a2.5 2.5 0 0 1-5 0H7a2.5 2.5 0 0 1-5 0H1v-2h14V7zm3 2v3h3l-1.5-2L18 9zM4.5 14a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>' +
      '</svg>';
    // Gift icon
    var giftSvg = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
      '<path d="M20 7h-2.5c.3-.4.5-.9.5-1.5C18 4.1 16.9 3 15.5 3c-.9 0-1.7.5-2.2 1.3L12 6l-1.3-1.7C10.2 3.5 9.4 3 8.5 3 7.1 3 6 4.1 6 5.5c0 .6.2 1.1.5 1.5H4c-.6 0-1 .4-1 1v3c0 .6.4 1 1 1h1v8c0 .6.4 1 1 1h12c.6 0 1-.4 1-1v-8h1c.6 0 1-.4 1-1V8c0-.6-.4-1-1-1zm-4.5-2.5c.6 0 1 .5 1 1s-.4 1-1 1-1-.5-1-1 .4-1 1-1zm-7 0c.6 0 1 .5 1 1s-.4 1-1 1-1-.5-1-1 .4-1 1-1zM5 9h6v2H5V9zm2 11v-7h4v7H7zm6 0v-7h4v7h-4zm6-9h-6V9h6v2z"/>' +
      '</svg>';

    return ''
      + '<div class="' + textClass + '">' + text + '</div>'
      + '<div class="syba-gift-pb__track">'
      +   '<div class="syba-gift-pb__fill" style="width:' + fillPct.toFixed(2) + '%"></div>'
      +   '<div class="syba-gift-pb__dot syba-gift-pb__dot--ship' + shipReached + '" style="left:50%">' + truckSvg + '</div>'
      +   '<div class="syba-gift-pb__dot syba-gift-pb__dot--gift' + giftReached + '" style="left:100%">' + giftSvg + '</div>'
      +   '<div class="syba-gift-pb__lbl syba-gift-pb__lbl--ship' + shipReached + '" style="left:50%">' + COPY.label_shipping + '</div>'
      +   '<div class="syba-gift-pb__lbl syba-gift-pb__lbl--gift' + giftReached + '" style="left:100%">' + COPY.label_gift
      +     '<span class="syba-gift-pb__new">' + (COPY.badge_new || 'NEW') + '</span>'
      +   '</div>'
      + '</div>';
  }

  /* ---- DOM helpers ------------------------------------------------- */
  function findDrawer() {
    // Kaching's drawer is rendered into one of these roots. Try in
    // order of specificity. The web-component tag is most stable.
    return document.querySelector('kaching-cart-drawer')
        || document.querySelector('.kaching-cart')
        || document.querySelector('[class*="kaching-cart"]');
  }

  function findKachingBar(drawer) {
    // Locate Kaching's own progress/promotions bar so we can (a) slot
    // ours into the exact same DOM position — guaranteeing it sits
    // below the header and above the items, same as Kaching's — and
    // (b) hide theirs.
    var sels = [
      '[class*="tiered-promotions"]',
      '[class*="promotions-progress"]',
      '[class*="promotions-bar"]',
      '[class*="goal-bar"]'
    ];
    for (var i = 0; i < sels.length; i++) {
      var nodes = drawer.querySelectorAll(sels[i]);
      for (var j = 0; j < nodes.length; j++) {
        var n = nodes[j];
        if (!n.classList.contains(INJECTED_CLASS) && !n.closest('.' + INJECTED_CLASS)) {
          return n;
        }
      }
    }
    return null;
  }

  function insertBar(drawer, wrapper, kachingBar) {
    // Primary: drop our bar immediately before Kaching's bar so we
    // inherit the correct slot (below "My cart • X" header, above the
    // items). This is the fix for the bar overlapping the header.
    if (kachingBar && kachingBar.parentNode) {
      kachingBar.parentNode.insertBefore(wrapper, kachingBar);
      return;
    }
    // Fallback A: right after the cart header.
    var header = drawer.querySelector(
      '[class*="kaching-cart__header"], [class*="kaching-cart-header"], [class*="cart-drawer__header"]'
    );
    if (header && header.parentNode) {
      if (header.nextSibling) {
        header.parentNode.insertBefore(wrapper, header.nextSibling);
      } else {
        header.parentNode.appendChild(wrapper);
      }
      return;
    }
    // Fallback B: before the items container.
    var items = drawer.querySelector(
      '[class*="item-container"], [class*="cart-items"], [class*="cart__items"]'
    );
    if (items && items.parentNode) {
      items.parentNode.insertBefore(wrapper, items);
      return;
    }
    // Last resort.
    drawer.insertBefore(wrapper, drawer.firstChild);
  }

  function hideKachingBarImperative(drawer, kachingBar) {
    // CSS handles the common cases; this is a fallback for class names
    // not caught by the stylesheet's [class*=...] selectors.
    if (kachingBar) { kachingBar.style.display = 'none'; }
    var sels = [
      '[class*="tiered-promotions"]',
      '[class*="promotions-progress"]',
      '[class*="promotions-bar"]',
      '[class*="goal-bar"]'
    ];
    for (var i = 0; i < sels.length; i++) {
      var nodes = drawer.querySelectorAll(sels[i]);
      for (var j = 0; j < nodes.length; j++) {
        if (!nodes[j].classList.contains(INJECTED_CLASS) && !nodes[j].closest('.' + INJECTED_CLASS)) {
          nodes[j].style.display = 'none';
        }
      }
    }
  }

  /* ---- Footer shipping line + checkout-button total ----------------
     Injects a "Shipping" row under Kaching's subtotal and folds the
     shipping fee into the checkout button's total. Driven by the same
     per-_geo_country config (ship_cost_cents + shipping_threshold_cents).

     Free above threshold (struck-through original + "Free"); paid below.
     The button total is an ESTIMATE (subtotal + standard primary-carrier
     shipping) — real checkout shipping is address/weight-based, accepted
     by the merchant.

     Kaching footer DOM (verified 2026-05-21):
       .kaching-cart__footer
         .kaching-cart__subtotal              (flex, space-between, 15px/600)
         .kaching-cart__checkout-button
           …__checkout-button-label           ("Secured Checkout • €X") */
  var SHIP = window.SybaShipCopy || { label: 'Shipping', free: 'Free' };
  var SHIP_ROW = 'syba-ship-row';

  /* ---- Welcome Bundle free-shipping grant ---------------------------
     Checkout grants FREE shipping in EVERY market when the cart holds an
     M/L Welcome Bundle — enforced server-side by the shipping app's
     starter-bundle-free-shipping discount function. The drawer must show
     the same reality, otherwise an M-bundle cart in a €70 market says
     "Add €7.01 to unlock free shipping" while checkout is already free.

     Client mirror of the function's gate, from /cart.js line items:
       - product handle ends in "-starter-bundle" (exactly the 5 Welcome
         Bundles; the also-tagged try-out-bundle has a different handle
         AND a Default Title variant, so it can't slip in), and
       - variant title starts "M (" / "L (" (S keeps paying, everywhere).
     Same fail-closed direction as the function: a renamed handle/variant
     just re-locks the bar (understates), it never promises a free ship
     that checkout won't honor. */
  var GRANT_TITLE_RE = /^[ML] \(/;
  var GRANT_HANDLE_RE = /-starter-bundle$/;
  var cartHasGrantedBundle = false;
  // Update the grant flag from any cart object that carries line items
  // (/cart.js always does; Kaching's getCartData may not — then keep the
  // last known value). Returns true when the flag flipped, and forces the
  // bar to redraw since its unlock state changed independently of total.
  function noteGrantedBundle(cartObj) {
    if (!cartObj || !Array.isArray(cartObj.items)) return false;
    var granted = false;
    for (var i = 0; i < cartObj.items.length; i++) {
      var it = cartObj.items[i];
      if (it && (it.quantity || 0) > 0 &&
          GRANT_HANDLE_RE.test(String(it.handle || '')) &&
          GRANT_TITLE_RE.test(String(it.variant_title || ''))) {
        granted = true;
        break;
      }
    }
    if (granted !== cartHasGrantedBundle) {
      cartHasGrantedBundle = granted;
      lastRenderedTotal = -1; // bar unlock state changed; total may not have
      return true;
    }
    return false;
  }

  // Cart-level "requires shipping". FALSE when the cart contains only no-ship
  // products (gift cards, digital items, services). Shopify's /cart.js and
  // Kaching's cart data both expose `requires_shipping`; checkout already
  // charges €0 shipping for such carts, so our cart module must mirror that
  // (live fix). Default true (charge) until a cart read tells us otherwise.
  var cartRequiresShipping = true;
  // Does the cart hold ANY line? An EMPTY cart also reports
  // requires_shipping === false, so the two must be told apart: a gift-card-only
  // cart has no reward left to chase (bar comes down), while an empty cart is
  // exactly where the "Add €50 to unlock free shipping" nudge belongs. Default
  // true so the bar renders until a cart read says the cart is empty.
  var cartHasItems = true;
  // Update both flags from any cart object we read. Returns true if either
  // changed (caller may re-render). Safe: only acts on explicit signals, so a
  // partial cart object (Kaching's sync data may carry no `items`) leaves the
  // last known value alone.
  function noteRequiresShipping(cartObj) {
    var changed = false;
    if (cartObj && typeof cartObj.requires_shipping === 'boolean') {
      if (cartRequiresShipping !== cartObj.requires_shipping) {
        cartRequiresShipping = cartObj.requires_shipping;
        changed = true;
      }
    }
    var has = null;
    if (cartObj && typeof cartObj.item_count === 'number') {
      has = cartObj.item_count > 0;
    } else if (cartObj && Array.isArray(cartObj.items)) {
      has = cartObj.items.length > 0;
    }
    if (has !== null && has !== cartHasItems) {
      cartHasItems = has;
      changed = true;
    }
    return changed;
  }

  function shipCostCents() {
    return (typeof cfg.ship_cost_cents === 'number') ? cfg.ship_cost_cents : 0;
  }
  // "Free" when the cart needs no shipping at all (no-ship products), OR an
  // M/L Welcome Bundle granted it (checkout-enforced, all markets), OR the
  // subtotal cleared the per-country free-shipping threshold.
  function shippingIsFree(totalCents) {
    return !cartRequiresShipping || cartHasGrantedBundle ||
           totalCents >= shipThresholdCents();
  }

  function renderShippingRow(totalCents) {
    var footer = document.querySelector('.kaching-cart__footer');
    if (!footer) return;
    var subtotal = footer.querySelector('.kaching-cart__subtotal');
    if (!subtotal) return;

    var free = shippingIsFree(totalCents);
    var costStr = formatMoney(shipCostCents());
    // No-shipping cart (gift cards / digital): plain "Free" — there is no
    // shipping fee to strike through. Threshold-free: show the struck cost.
    var valHTML = !cartRequiresShipping
      ? SHIP.free
      : (free
          ? '<s class="' + SHIP_ROW + '__strike">' + costStr + '</s> ' + SHIP.free
          : costStr);
    var cls = SHIP_ROW + (free ? ' ' + SHIP_ROW + '--free' : '');

    var existing = footer.querySelector('.' + SHIP_ROW);
    if (existing) {
      // Idempotent: only write when the value actually changes. Writing
      // className/innerHTML unconditionally on every render fed the drawer
      // MutationObserver (childList/subtree) → queueRender → render → write →
      // a feedback loop that, with manageGift's per-render /cart.js fetch,
      // kept the drawer perpetually busy and blocked cart edits (e.g. a qty
      // change after removing a discount). Guard like updateCheckoutTotal /
      // renderDeliveryLine already do.
      if (existing.className !== cls) existing.className = cls;
      var v = existing.querySelector('.' + SHIP_ROW + '__val');
      if (v && v.innerHTML !== valHTML) v.innerHTML = valHTML;
      // Re-anchor: keep our row DIRECTLY after the subtotal even when Kaching
      // rebuilds/re-orders the footer on add/remove. Without this, Kaching can
      // re-insert its subtotal node after our (surviving) row, so the Subtotal
      // and Shipping lines visibly swap places as you change the cart (live
      // bug #3). Guarded so it only moves when actually displaced — no move =
      // no mutation = no observer churn (and doRender pauses the observer
      // around our writes anyway). Loop-safe even if our row is in a different
      // parent than the subtotal: insertBefore reparents it to the subtotal's
      // parent, so the very next render sees previousElementSibling===subtotal
      // and stops — at most one corrective move, never a per-render loop.
      if (existing.previousElementSibling !== subtotal) {
        subtotal.parentNode.insertBefore(existing, subtotal.nextSibling);
      }
      return;
    }
    var row = document.createElement('div');
    row.className = cls;
    row.innerHTML = '<span class="' + SHIP_ROW + '__label">' + SHIP.label + '</span>'
                  + '<span class="' + SHIP_ROW + '__val">' + valHTML + '</span>';
    // Insert directly after the subtotal row (footer gap handles spacing).
    subtotal.parentNode.insertBefore(row, subtotal.nextSibling);
  }

  function updateCheckoutTotal(totalCents) {
    var label = document.querySelector('.kaching-cart__checkout-button-label');
    if (!label) return;
    var ship = shippingIsFree(totalCents) ? 0 : shipCostCents();
    var newTotal = formatMoney(totalCents + ship);
    // Rewrite the cart total in the (localized) label while preserving the
    // "Secured Checkout • " prefix in whatever language it's in. Replace the
    // LAST money token, not the first — the total always sits at the end of
    // the label ("… • €X"), so this stays correct even if a future
    // localized prefix were to contain a number.
    var cur = label.textContent;
    var matches = cur.match(/€\s?[\d.,]+|[\d.,]+\s?kr/gi);
    if (!matches || !matches.length) return;
    var last = matches[matches.length - 1];
    var idx = cur.lastIndexOf(last);
    var next = cur.slice(0, idx) + newTotal + cur.slice(idx + last.length);
    // Guard: only write when it actually changes, so our own mutation
    // doesn't loop the observer.
    if (cur !== next) label.textContent = next;
  }

  /* ---- Estimated-delivery line (under the checkout button) ----------
     The store runs the Omega "Estimated Delivery Date" app, which
     IP-geolocates the visitor and applies per-country rules. Rather than
     make Omega RENDER its widget into the drawer (the old approach —
     injecting <div class="ot-estimated-shipping"> + nudging the
     undocumented estimatedRenderApp()), which:
       - required Omega's position set to "All pages" (that collided with
         the PDP product widget → flicker/disappear on live)
       - rendered Omega's PDP "shipping-methods + live countdown" markup,
         far too long for the narrow drawer (wrapped to 3 lines, split the
         countdown)
     …we now READ Omega's already-computed, country-aware values from
     window.estimatedCurrentRuleVariables and render our OWN clean,
     single-line markup. Omega computes these globally on every page even
     with position = "Product only" (verified 2026-05-21), so no app
     setting dependency and no widget-render side effects.

     Date-only per merchant choice (no live countdown) — matches Omega's
     own intended cart format and guarantees one line. The date string
     comes from Omega already localized to the page locale; we only
     localize the "98% chance of delivery " prefix (window.SybaGiftCopy).

     estimatedCurrentRuleVariables is an undocumented Omega global — read
     defensively (try/catch + presence checks); if it ever goes away the
     line simply doesn't render (no error, drawer unaffected). */
  var OMEGA_WRAP = 'syba-omega-edd';
  var deliveryRetryTimer = null;

  function getOmegaDelivery() {
    try {
      var rv = window.estimatedCurrentRuleVariables;
      if (!rv) return null;
      var min = rv['{delivery_min}'] && rv['{delivery_min}'].text;
      var max = rv['{delivery_max}'] && rv['{delivery_max}'].text;
      if (!min) return null;
      return { min: min, max: max };
    } catch (e) { return null; }
  }

  function renderDeliveryLine() {
    var footer = document.querySelector('.kaching-cart__footer');
    if (!footer) return;
    var d = getOmegaDelivery();
    if (!d) {
      // Omega's rule loads async (~500ms). Retry a few times until present.
      if (deliveryRetryTimer) return; // one timer only
      deliveryRetryTimer = setTimeout(function () {
        deliveryRetryTimer = null;
        renderDeliveryLine();
      }, 500);
      return;
    }
    var prefix = (window.SybaGiftCopy && window.SybaGiftCopy.delivery_prefix) || '98% chance of delivery ';
    var dateStr = (d.max && d.max !== d.min) ? (d.min + ' – ' + d.max) : d.min;
    var html = '<span class="' + OMEGA_WRAP + '__icon" aria-hidden="true">🌿</span>'
             + '<span class="' + OMEGA_WRAP + '__text">' + prefix + '<strong>' + dateStr + '</strong></span>';
    var wrap = footer.querySelector('.' + OMEGA_WRAP);
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = OMEGA_WRAP + ' is-filled';
      footer.appendChild(wrap); // under the checkout button (last footer child)
    } else {
      wrap.classList.add('is-filled');
    }
    if (wrap.innerHTML !== html) wrap.innerHTML = html;
  }

  /* ---- Mystery Gift auto-add / auto-remove / qty-lock (phase 2) -----
     When cfg.auto_manage_gift is true, THIS engine owns the free Mystery
     Gift (variant cfg.gift_variant_id, €0) — keyed to the EFFECTIVE gift
     threshold giftThresholdCents() (per-country table, floored at the
     effective free-shipping threshold + gift_min_gap_cents in EUR markets),
     NOT Kaching's market context. REQUIRED pairing:
     Kaching's own free-gift promo must be disabled in the dashboard, else
     both manage the same variant and fight.

     Rules:
       - eligible (non-gift subtotal >= giftThresholdCents()) AND no gift line
         → add it (with line property _free_gift for identification)
       - NOT eligible AND gift line present → remove it
       - eligible AND gift present but qty != 1 → reset to 1 (lock)
     Loop-safe: a single in-flight lock (giftOpInProgress) + we only act
     when the cart state differs from the desired state. The gift is €0 so
     adding/removing it never changes the cart total → no feedback loop on
     the threshold. Re-evaluated when the (non-gift) total changes, and when
     the threshold itself moves (applyCountry, SybaShipThreshold.onChange
     both reset lastGiftTotal + lastManageTotal). */
  var GIFT_VARIANT = cfg.gift_variant_id;
  var GIFT_MARKER = cfg.gift_marker || 'mystery_v1';
  var giftOpInProgress = false;
  // Last evaluated gift-eligibility key: "<non-gift cents>:s|n" (s = the cart
  // still holds a shippable non-gift line). -1 = never evaluated / force
  // re-evaluation; it can never equal a key, so any reset re-opens the gate.
  var lastGiftTotal = -1;
  // Outer fetch-gate: the cart total at our last /cart.js evaluation. doRender
  // calls manageGift on EVERY drawer mutation; without this gate each render
  // fired a /cart.js fetch, and the drawer MutationObserver re-triggers render
  // very frequently → a /cart.js request storm that Shopify rate-limits (HTTP
  // 429), which breaks the whole cart (qty edits, gift add/remove all fail).
  // The gift only needs re-evaluation when the cart total moves; the €0 gift
  // never moves the total, so gating here never suppresses a needed add/remove.
  var lastManageTotal = -1;

  function postCart(url, body, done) {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function () {
      giftOpInProgress = false;
      // Refresh Kaching's drawer so the line appears/disappears immediately.
      try { if (window.kachingCartApi && window.kachingCartApi.refreshCart) window.kachingCartApi.refreshCart(); } catch (e) {}
      if (done) done();
    }).catch(function () {
      giftOpInProgress = false;
      lastGiftTotal = -1;   // allow a retry on the next cart change
      lastManageTotal = -1; // re-open the fetch-gate so the retry can run
    });
  }

  function manageGift(totalCents) {
    if (giftOpInProgress) return;
    // Fetch-gate (see lastManageTotal): skip the /cart.js fetch entirely when
    // the cart total hasn't moved since our last evaluation. This is the
    // primary defense against the /cart.js 429 storm — without it, every
    // drawer mutation triggered a fetch.
    if (typeof totalCents === 'number') {
      if (totalCents === lastManageTotal) return;
      lastManageTotal = totalCents;
    }
    // NOTE: the fetch below runs even when auto_manage_gift is OFF. It is the
    // post-init freshness path for the country + no-shipping + bundle-grant
    // flags (Kaching's sync data may lack `items`), and the bundle grant must
    // re-lock promptly when the bundle leaves the cart — free-shipping display
    // may never overstate what checkout charges. Gift add/remove/lock is the
    // only part gated on the setting. Request rate is unchanged: the
    // total-move gate above is the storm protection, not the setting.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (!c || !c.items) return;
        // Piggyback this fetch to keep the active country, the no-shipping
        // flag and the bundle grant in sync (no extra request). If any
        // changed, redraw next frame.
        if (applyCountry(c.attributes && c.attributes._geo_country)) { queueRender(); }
        if (noteRequiresShipping(c)) { queueRender(); }
        if (noteGrantedBundle(c)) { queueRender(); }
        if (!cfg.auto_manage_gift || !GIFT_VARIANT) return; // flags stay fresh; gift ops end here
        var gift = null, nonGiftCents = 0, shippableNonGift = false;
        c.items.forEach(function (it) {
          if (it.variant_id === GIFT_VARIANT) { gift = it; return; }
          nonGiftCents += it.final_line_price;
          // Shopify sets requires_shipping=false on gift cards and other
          // no-ship items. Treat anything that doesn't say false as shippable
          // (fail-closed: a missing flag keeps today's behaviour).
          if (it.requires_shipping !== false) { shippableNonGift = true; }
        });
        // A cart of ONLY no-ship lines (digital gift cards) must never get the
        // physical Mystery Gift. Reported live 2026-09-07 (Gorgias 238661157)
        // and reproduced: €50 + €30 gift cards clear the €75 gift threshold →
        // we add the gift → the cart flips requires_shipping false→true, so
        // checkout starts asking for a shipping address and prices a rate. The
        // rate lands at the PAID tier (€5.95 in NL) because Shopify's
        // price-based rate conditions only count items that require shipping,
        // and the €0 gift is the only one — so the €80 of gift-card value does
        // not reach the €50 free-shipping band. The customer cannot escape it
        // either: deleting the gift line lets the next cart read re-add it.
        // Gate the ADD on a real shippable line, and keep !eligible so an
        // already-present gift is removed when the last shippable line leaves.
        var eligible = shippableNonGift && nonGiftCents >= giftThresholdCents();
        // Debounce: only re-evaluate when the non-gift total actually moved
        // (adding/removing the €0 gift doesn't move it → no loop). Keyed on the
        // shippable flag too, so swapping a physical line for an equally-priced
        // gift card (same total, eligibility flipped) is still evaluated.
        var giftKey = nonGiftCents + (shippableNonGift ? ':s' : ':n');
        if (giftKey === lastGiftTotal) return;
        lastGiftTotal = giftKey;

        if (eligible && !gift) {
          giftOpInProgress = true;
          postCart('/cart/add.js', { items: [{ id: GIFT_VARIANT, quantity: 1, properties: { _free_gift: GIFT_MARKER } }] });
        } else if (!eligible && gift) {
          giftOpInProgress = true;
          postCart('/cart/change.js', { id: gift.key, quantity: 0 });
        } else if (eligible && gift && gift.quantity !== 1) {
          giftOpInProgress = true;
          postCart('/cart/change.js', { id: gift.key, quantity: 1 });
        }
      })
      .catch(function () { /* non-fatal; next cart change retries */ });
  }

  /* ---- Free-gift line: make the title non-clickable (live #4) --------
     The Mystery Gift is a €0 line the customer didn't choose to browse to, so
     its title shouldn't link to the product page. Kaching renders each line's
     title as an <a class="kaching-cart-item__title" href="…?variant=ID">; we
     find the line whose variant matches GIFT_VARIANT and neutralize it.
     We DON'T strip the href (Kaching would just rebuild it on the next
     re-render → observer churn). pointer-events:none + tabindex=-1 blocks both
     mouse and keyboard activation; all writes are guarded so a settled line
     produces no mutation. Decorative, never throws. */
  function lockGiftLink() {
    if (!GIFT_VARIANT) return;
    var items = document.querySelectorAll('.kaching-cart-item');
    for (var i = 0; i < items.length; i++) {
      var link = items[i].querySelector('.kaching-cart-item__title');
      if (!link) continue;
      var m = (link.getAttribute('href') || '').match(/[?&]variant=(\d+)/);
      // Number() coercion guards against GIFT_VARIANT ever being a string
      // (cfg.gift_variant_id is a JSON number today, so this is just insurance).
      if (!m || parseInt(m[1], 10) !== Number(GIFT_VARIANT)) continue;
      if (link.style.pointerEvents !== 'none') link.style.pointerEvents = 'none';
      if (link.style.cursor !== 'default') link.style.cursor = 'default';
      if (link.getAttribute('tabindex') !== '-1') link.setAttribute('tabindex', '-1');
    }
  }

  /* ---- Red "NEW" badge on the gift's own cart line (Autumn Mystery
     Gift launch, 2026-07). Same treatment as the try-out bundle page's
     "Best deal" badge (tryout-bundle-builder.css) but slightly less angled
     (-4deg vs -8deg — merchant request). Injection mirrors
     syba-cart-perliter-badge.js: identify the line by data-product-handle
     (same hook the qty-lock CSS uses), append once, idempotent. Runs inside
     doRender's pauseObs window so it never feeds the drawer observer. */
  function renderGiftLineBadge() {
    var items = document.querySelectorAll('.kaching-cart-item[data-product-handle="mystery-gift"]');
    for (var i = 0; i < items.length; i++) {
      if (items[i].querySelector('.syba-gift-new-badge')) continue;
      var b = document.createElement('div');
      b.className = 'syba-gift-new-badge';
      b.textContent = (COPY.badge_new || 'NEW');
      items[i].appendChild(b);
    }
  }

  /* ---- Render pipeline -------------------------------------------- */
  var lastRenderedTotal = -1;

  /* Drawer MutationObserver — assigned in init(), held at this scope so
     doRender can PAUSE it while we mutate the drawer. Our own DOM writes
     (shipping row, checkout total, bar) would otherwise re-trigger the
     observer → queueRender → doRender → write → … a feedback loop that kept
     the drawer perpetually busy and blocked cart edits (e.g. changing a
     quantity after removing a discount). Pausing during our writes breaks it
     while still letting genuine Kaching re-renders trigger us. */
  var drawerObs = null;
  var observedDrawer = null;
  var OBS_OPTS = { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-cart-count'] };
  function pauseObs() { try { if (drawerObs) drawerObs.disconnect(); } catch (e) {} }
  function resumeObs() {
    try {
      if (drawerObs && observedDrawer) {
        drawerObs.takeRecords(); // discard records queued by our own mutations
        drawerObs.observe(observedDrawer, OBS_OPTS);
      }
    } catch (e) {}
  }

  function doRender(drawer, totalCents) {
    pauseObs(); // our DOM writes below must not re-trigger our own observer
    try {
      // Footer shipping + button total run on EVERY render (Kaching may
      // re-render the footer without a cart-total change). All idempotent.
      try { renderShippingRow(totalCents); } catch (e) { /* never break drawer */ }
      try { updateCheckoutTotal(totalCents); } catch (e) { /* never break drawer */ }
      // Estimated-delivery line — reads Omega's value + renders our markup.
      try { renderDeliveryLine(); } catch (e) { /* never break drawer */ }
      // Mystery Gift auto-add/remove (async fetch; no synchronous DOM write).
      // Pass totalCents so manageGift's fetch-gate can skip the /cart.js call
      // when the cart hasn't changed (storm prevention).
      try { manageGift(totalCents); } catch (e) { /* never break drawer */ }
      // Neutralize the free-gift line's title link. Runs on every render
      // (before the bar early-return) because the €0 gift can appear without
      // moving the total — so the bar-unchanged early-return below must not
      // skip it. Idempotent.
      try { lockGiftLink(); } catch (e) { /* never break drawer */ }
      // Red "NEW" badge on the gift line. Same every-render treatment as
      // lockGiftLink (the €0 gift line can appear without a total change).
      try { renderGiftLineBadge(); } catch (e) { /* never break drawer */ }

      var kachingBar = findKachingBar(drawer);
      var existing = drawer.querySelector('.' + INJECTED_CLASS);

      /* No-ship cart (digital gift cards only): there is no reward left to
         chase. Shipping is already free because nothing ships, and manageGift
         deliberately withholds the physical Mystery Gift (a gift-card-only
         cart that receives it starts paying a delivery rate — see the comment
         there). The bar would still read "All rewards unlocked! 🎉 … [seasonal]
         Mystery Gift", i.e. promise a gift that will never arrive, so take it
         down instead. Kaching's own bar stays hidden, as always.
         lastRenderedTotal resets so the bar is rebuilt from scratch the moment
         a shippable line joins the cart.

         cartHasItems is part of the gate because an EMPTY cart ALSO reports
         requires_shipping === false. Without it this branch silently took the
         bar off the empty drawer too — where "Add €50 to unlock free shipping"
         is the whole point. */
      if (!cartRequiresShipping && cartHasItems) {
        if (existing && existing.parentNode) {
          existing.parentNode.removeChild(existing);
        }
        lastRenderedTotal = -1;
        hideKachingBarImperative(drawer, kachingBar);
        return;
      }

      if (existing && totalCents === lastRenderedTotal) {
        hideKachingBarImperative(drawer, kachingBar);
        return;
      }
      lastRenderedTotal = totalCents;

      var html = buildBarHTML(totalCents);
      if (existing) {
        existing.innerHTML = html;
      } else {
        var wrapper = document.createElement('div');
        wrapper.className = INJECTED_CLASS;
        wrapper.innerHTML = html;
        insertBar(drawer, wrapper, kachingBar);
      }
      // Hide Kaching's bar AFTER inserting ours next to it.
      hideKachingBarImperative(drawer, kachingBar);
    } finally {
      resumeObs(); // re-arm for genuine Kaching changes
    }
  }

  function render() {
    var drawer = findDrawer();
    if (!drawer) return;

    var sync = getCartTotalSync();
    if (sync !== null) {
      doRender(drawer, sync);
      return;
    }

    // Async path — first paint, or Kaching API not loaded yet.
    getCartTotalAsync().then(function (totalCents) {
      var d = findDrawer();
      if (d) doRender(d, totalCents);
    });
  }

  /* ---- Render trigger. Coalesced via requestAnimationFrame so the
     MutationObserver doesn't fire render() hundreds of times per Kaching
     DOM update, while still applying our footer changes (shipping row +
     button total) on the very NEXT frame after Kaching renders the footer
     — instead of a 60ms gap that made the button visibly jump from the
     bare subtotal to subtotal+shipping. rAF naturally batches multiple
     mutations into one render per frame. */
  var renderQueued = false;
  function queueRender() {
    if (renderQueued) return;
    renderQueued = true;
    var run = function () {
      renderQueued = false;
      try { render(); } catch (e) { /* never break the drawer */ }
    };
    if (window.requestAnimationFrame) { window.requestAnimationFrame(run); }
    else { setTimeout(run, 16); }
  }

  /* ---- Bootstrap. Watch for the drawer DOM to appear, then watch
     it for content changes. Two-stage observer keeps the cost low —
     once the drawer exists we stop observing the whole body. */
  function init() {
    // FAST PATH: apply the cached/published country BEFORE the first render so
    // the very first drawer paint already shows the visitor's per-country
    // ship_cost_cents (e.g. NL €5.95) instead of the SSR-baked Ireland default
    // (€9.95). Synchronous — no network. No-op if nothing cached yet (first-
    // ever visit), where the bridge event + resolveCountry below take over.
    applyCountry(cachedIso());

    queueRender();

    // Intelligems free-shipping group settled or changed: the shipping
    // milestone moved, so force a redraw (total may not have changed).
    try {
      if (window.SybaShipThreshold && typeof window.SybaShipThreshold.onChange === 'function') {
        window.SybaShipThreshold.onChange(function () {
          lastRenderedTotal = -1;
          // The gift threshold follows the ship threshold (giftThresholdCents),
          // so a resolver change with an UNCHANGED cart must still re-run
          // manageGift: re-open both of its gates (total + eligibility key).
          lastGiftTotal = -1;
          lastManageTotal = -1;
          queueRender();
        });
      }
    } catch (e) {}

    // Re-resolve as soon as the geo bridge publishes the detected country on
    // THIS page (first-ever visit: edge-geo returns after init). Fires before
    // — and independently of — the _geo_country cart-attribute round-trip, so
    // the offer corrects without waiting for /cart/update.js.
    try {
      window.addEventListener('syba:geo-country', function (e) {
        var iso = (e && e.detail) || cachedIso();
        if (applyCountry(iso)) { queueRender(); }
      });
    } catch (e) {}

    // Authoritative sync from the cart attribute (covers an explicit picker
    // choice + keeps state correct). The geo bridge writes _geo_country
    // asynchronously, so if it isn't there yet, retry once shortly after.
    resolveCountry(function (changed) {
      if (changed) { queueRender(); return; }
      setTimeout(function () { resolveCountry(function (c2) { if (c2) queueRender(); }); }, 800);
    });

    var bodyObs;

    function attachDrawerObs(drawer) {
      if (drawerObs) return; // drawerObs is module-scoped (see pauseObs/resumeObs)
      observedDrawer = drawer;
      drawerObs = new MutationObserver(queueRender);
      drawerObs.observe(drawer, OBS_OPTS);
    }

    function bodyTick() {
      var drawer = findDrawer();
      if (drawer) {
        attachDrawerObs(drawer);
        queueRender();
        if (bodyObs) { try { bodyObs.disconnect(); } catch (e) {} bodyObs = null; }
      }
    }

    var initialDrawer = findDrawer();
    if (initialDrawer) {
      attachDrawerObs(initialDrawer);
    } else {
      try {
        bodyObs = new MutationObserver(bodyTick);
        bodyObs.observe(document.body, { childList: true, subtree: true });
      } catch (e) { /* MutationObserver unavailable */ }
    }

    /* ---- Also listen for cart-mutation events that Shopify/Kaching
       emit. Any of these signals a cart change, so re-render. Keep
       both event names because different versions of Kaching dispatch
       different ones. */
    var EVT = ['cart:update', 'cart:refresh', 'cart:item-added', 'cart:item-removed'];
    for (var i = 0; i < EVT.length; i++) {
      try { document.addEventListener(EVT[i], queueRender); } catch (e) {}
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
